import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Mail, Check, Plus, X, Calendar, AlertCircle, Clock, Users, Info } from 'lucide-react'
import { api } from '../../api/client'

interface Connector {
  id: string
  provider: string
  is_connected: boolean
  scopes: string[]
  provider_email: string | null
  last_sync_at: string | null
  error_message: string | null
}

type ScopeState = 'connected' | 'not_connected' | 'missing_scope' | 'error'
interface CalendarStatus {
  state: ScopeState
  scope_granted?: boolean
  message?: string
}
interface ContactsStatus {
  state: ScopeState
  scope_granted?: boolean
  explicit?: boolean
  other?: boolean
  message?: string
}

type BackfillStatus = 'pending' | 'running' | 'completed' | 'failed'
interface BackfillState {
  has_any: boolean
  processed_since: string | null
  latest_status: BackfillStatus | null
  latest_run_at: string | null
  latest_error_message?: string | null
  events_ingested_total: number
  events_seen_total?: number  // contacts-only, for noise diagnostics
}

// Email (Postman) has no backfill shape — it's continuous ingestion via
// a scheduled agent. The "status" collapses the agent row + recent run
// counts + recent memory counts into a single struct the UI can render
// equivalent to the calendar/contacts rows.
type EmailState = 'active' | 'inactive' | 'no_postman' | 'gmail_not_connected'
interface EmailStatus {
  state: EmailState
  schedule?: string | null
  is_active?: boolean
  last_run_at?: string | null
  last_run_status?: string | null
  next_run_at?: string | null
  emails_last_24h?: number
  total_email_memories?: number
  run_count_7d?: number
  run_ok_7d?: number
}


export function ConnectorsPage() {
  const [connectors, setConnectors] = useState<Connector[]>([])
  const [calendarStatus, setCalendarStatus] = useState<CalendarStatus | null>(null)
  const [calendarBackfill, setCalendarBackfill] = useState<BackfillState | null>(null)
  const [contactsStatus, setContactsStatus] = useState<ContactsStatus | null>(null)
  const [contactsBackfill, setContactsBackfill] = useState<BackfillState | null>(null)
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null)
  const [emailBusy, setEmailBusy] = useState(false)
  const [extendOpen, setExtendOpen] = useState(false)
  const [extendBusy, setExtendBusy] = useState(false)
  const [contactsBusy, setContactsBusy] = useState(false)
  const [permsOpen, setPermsOpen] = useState(false)
  const [, setLoading] = useState(true)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const calendarPollTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const contactsPollTimer = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    loadConnectors()
    const connected = searchParams.get('connected')
    const error = searchParams.get('error')
    if (connected) {
      setToast({ type: 'success', message: `${connected} ligado com sucesso.` })
      setSearchParams({})
      setTimeout(() => setToast(null), 4000)
    } else if (error) {
      setToast({ type: 'error', message: `Erro: ${error}` })
      setSearchParams({})
      setTimeout(() => setToast(null), 5000)
    }
  }, [])

  // Poll calendar backfill status while a run is pending/running.
  useEffect(() => {
    const active = calendarBackfill?.latest_status === 'pending' || calendarBackfill?.latest_status === 'running'
    if (active && !calendarPollTimer.current) {
      calendarPollTimer.current = setInterval(() => { refreshCalendarBackfillStatus() }, 5000)
    } else if (!active && calendarPollTimer.current) {
      clearInterval(calendarPollTimer.current)
      calendarPollTimer.current = null
    }
    return () => {
      if (calendarPollTimer.current) { clearInterval(calendarPollTimer.current); calendarPollTimer.current = null }
    }
  }, [calendarBackfill?.latest_status])

  // Poll contacts backfill status while a run is pending/running.
  useEffect(() => {
    const active = contactsBackfill?.latest_status === 'pending' || contactsBackfill?.latest_status === 'running'
    if (active && !contactsPollTimer.current) {
      contactsPollTimer.current = setInterval(() => { refreshContactsBackfillStatus() }, 5000)
    } else if (!active && contactsPollTimer.current) {
      clearInterval(contactsPollTimer.current)
      contactsPollTimer.current = null
    }
    return () => {
      if (contactsPollTimer.current) { clearInterval(contactsPollTimer.current); contactsPollTimer.current = null }
    }
  }, [contactsBackfill?.latest_status])

  async function loadConnectors() {
    try {
      const data = await api.get<Connector[]>('/connectors')
      setConnectors(data)
    } catch { /* ignore */ }
    try {
      const cal = await api.get<CalendarStatus>('/connectors/calendar/status')
      setCalendarStatus(cal)
    } catch { /* ignore */ }
    try {
      const cts = await api.get<ContactsStatus>('/connectors/contacts/status')
      setContactsStatus(cts)
    } catch { /* ignore */ }
    await refreshCalendarBackfillStatus()
    await refreshContactsBackfillStatus()
    await refreshEmailStatus()
    setLoading(false)
  }

  async function refreshEmailStatus() {
    try {
      const st = await api.get<EmailStatus>('/connectors/email/status')
      setEmailStatus(st)
    } catch { /* ignore */ }
  }

  async function triggerEmailRun() {
    setEmailBusy(true)
    try {
      await api.post('/connectors/email/trigger', {})
      setToast({ type: 'success', message: 'A processar o teu inbox agora…' })
      setTimeout(() => setToast(null), 4000)
      // Light delay then refresh — the Postman run takes ~10-30s; we pick
      // up the new last_run_at on the next status call.
      setTimeout(() => { refreshEmailStatus() }, 2000)
    } catch {
      setToast({ type: 'error', message: 'Não foi possível correr a triagem.' })
      setTimeout(() => setToast(null), 4000)
    } finally {
      setEmailBusy(false)
    }
  }

  async function refreshCalendarBackfillStatus() {
    try {
      const bf = await api.get<BackfillState>('/connectors/calendar/backfill')
      setCalendarBackfill(bf)
    } catch { /* ignore */ }
  }

  async function refreshContactsBackfillStatus() {
    try {
      const bf = await api.get<BackfillState>('/connectors/contacts/backfill')
      setContactsBackfill(bf)
    } catch { /* ignore */ }
  }

  async function connect(provider: string) {
    try {
      const data = await api.post<{ auth_url: string }>(`/connectors/${provider}/connect`)
      window.location.href = data.auth_url
    } catch { /* ignore */ }
  }

  async function disconnect(connectorId: string) {
    try {
      await api.delete(`/connectors/${connectorId}`)
      loadConnectors()
    } catch { /* ignore */ }
  }

  async function triggerBackfill(months: number) {
    setExtendBusy(true)
    try {
      await api.post<{ id: string; status: BackfillStatus }>(
        '/connectors/calendar/backfill',
        { months }
      )
      setToast({ type: 'success', message: `A processar ${months} ${months === 1 ? 'mês' : 'meses'} de histórico…` })
      setTimeout(() => setToast(null), 4000)
      await refreshCalendarBackfillStatus()
      setExtendOpen(false)
    } catch {
      setToast({ type: 'error', message: 'Não foi possível iniciar o backfill.' })
      setTimeout(() => setToast(null), 4000)
    } finally {
      setExtendBusy(false)
    }
  }

  async function triggerContactsBackfill() {
    setContactsBusy(true)
    try {
      await api.post<{ id: string; status: BackfillStatus }>(
        '/connectors/contacts/backfill',
        {}
      )
      setToast({ type: 'success', message: 'A processar os teus contactos do Google…' })
      setTimeout(() => setToast(null), 4000)
      await refreshContactsBackfillStatus()
    } catch {
      setToast({ type: 'error', message: 'Não foi possível sincronizar contactos.' })
      setTimeout(() => setToast(null), 4000)
    } finally {
      setContactsBusy(false)
    }
  }

  function formatDate(iso: string): string {
    try {
      const d = new Date(iso)
      return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })
    } catch { return iso }
  }

  /**
   * Relative-time formatter for the email status row.
   * We want natural phrases ("há 12 minutos", "ontem às 14:32") — not
   * timestamps — because the user is reading this as a liveness signal,
   * not an audit log.
   */
  function formatRelative(iso: string): string {
    try {
      const then = new Date(iso).getTime()
      const now = Date.now()
      const diffMin = Math.round((now - then) / 60000)
      if (diffMin < 1) return 'agora mesmo'
      if (diffMin < 60) return `há ${diffMin} min`
      const diffH = Math.round(diffMin / 60)
      if (diffH < 24) return `há ${diffH} h`
      const diffD = Math.round(diffH / 24)
      if (diffD === 1) return 'ontem'
      if (diffD < 7) return `há ${diffD} dias`
      return formatDate(iso)
    } catch { return iso }
  }

  const gmailConnectors = connectors.filter(c => c.provider === 'gmail' && c.is_connected)
  // Google Drive connector removed from the UI — we're not using Drive
  // ingestion for anything yet and surfacing the button invites users to
  // connect a scope the system doesn't act on. Backend endpoints stay,
  // we can bring the UI back later if we wire up a real use case.

  return (
    <div className="flex-1 overflow-y-auto" style={{ background: 'var(--cream)', fontFamily: 'var(--sans)' }}>
      {toast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 rounded-lg text-sm border"
          style={{
            background: '#fff',
            borderColor: toast.type === 'success' ? 'var(--border)' : '#fca5a5',
            color: toast.type === 'success' ? 'var(--ink-light)' : '#dc2626',
          }}
        >
          {toast.message}
        </div>
      )}

      <div className="max-w-lg mx-auto p-4 md:p-8">
        <h1 className="text-lg font-semibold mb-6" style={{ color: 'var(--ink)', fontFamily: 'var(--serif)' }}>Conectores</h1>

        <div className="space-y-4">

          {/* Gmail section */}
          <div className="bg-white rounded-lg border" style={{ borderColor: 'var(--border)' }}>
            <div className="p-4 flex items-center gap-3 border-b" style={{ borderColor: 'var(--border)' }}>
              <div className="p-2 rounded-md border shrink-0" style={{ background: 'var(--cream)', borderColor: 'var(--border)' }}>
                <Mail size={16} style={{ color: 'var(--ink-muted)' }} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium flex items-center gap-1.5" style={{ color: 'var(--ink)' }}>
                  Gmail
                  <button
                    onClick={() => setPermsOpen(v => !v)}
                    className="p-0.5 rounded transition-colors"
                    style={{ color: 'var(--ink-muted)' }}
                    onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink-light)')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-muted)')}
                    title="O que é que o Gmail precisa?"
                    aria-label="Mostrar permissões"
                  >
                    <Info size={12} />
                  </button>
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>Triagem de email, calendário e contactos do Google</p>
              </div>
            </div>

            {/* Permissions explainer — collapsed by default. Opens a single panel
                with one line per scope. Honest about what we ask and why, so the
                user doesn't need per-scope toggles (which wouldn't be real
                revocation anyway — Google only supports revoking all at once). */}
            {permsOpen && (
              <div
                className="px-4 py-3 border-b text-xs space-y-1.5"
                style={{ borderColor: 'var(--border)', background: 'var(--cream)' }}
              >
                <p style={{ color: 'var(--ink-muted)' }}>
                  Ao ligares o Gmail, o Google pede-te permissão para:
                </p>
                <ul className="space-y-1 pl-3" style={{ color: 'var(--ink-light)' }}>
                  <li>• <span style={{ color: 'var(--ink)' }}>Ler email</span> — triagem automática, distinguir newsletters de operacionais</li>
                  <li>• <span style={{ color: 'var(--ink)' }}>Criar rascunhos</span> — respostas propostas por ti, nunca enviadas sem tu aprovares</li>
                  <li>• <span style={{ color: 'var(--ink)' }}>Gerir etiquetas</span> — organizar conversas por tema, com a tua confirmação</li>
                  <li>• <span style={{ color: 'var(--ink)' }}>Calendário (leitura + escrita)</span> — briefings pré-reunião e propostas de eventos (que tu confirmas)</li>
                  <li>• <span style={{ color: 'var(--ink)' }}>Contactos + Other Contacts</span> — enriquecer a rede relacional com quem já interages</li>
                </ul>
                <p className="pt-1" style={{ color: 'var(--ink-muted)' }}>
                  Para revogar uma permissão individual, tens de ir a{' '}
                  <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
                    Conta Google → Aplicações com acesso
                  </a>. "Desligar" aqui remove tudo.
                </p>
              </div>
            )}

            {/* Connected accounts */}
            {gmailConnectors.map(connector => (
              <div key={connector.id} className="px-4 py-3 flex items-center justify-between border-b last:border-b-0" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2 min-w-0">
                  <Check size={12} style={{ color: 'var(--accent)' }} className="shrink-0" />
                  <span className="text-xs font-mono truncate" style={{ color: 'var(--ink-light)' }}>{connector.provider_email}</span>
                  {connector.error_message && (
                    <span className="text-xs text-red-500 truncate">{connector.error_message}</span>
                  )}
                </div>
                <button
                  onClick={() => disconnect(connector.id)}
                  className="ml-3 shrink-0 p-1 transition-colors rounded"
                  style={{ color: 'var(--ink-muted)' }}
                  title="Desligar"
                >
                  <X size={13} />
                </button>
              </div>
            ))}

            {/* Add account */}
            <div className="px-4 py-3">
              <button
                onClick={() => connect('gmail')}
                className="flex items-center gap-1.5 text-xs transition-colors"
                style={{ color: 'var(--ink-muted)' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink-light)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-muted)')}
              >
                <Plus size={12} />
                {gmailConnectors.length === 0 ? 'Ligar conta Gmail' : 'Adicionar outra conta Gmail'}
              </button>
            </div>

            {/* Calendar status strip — only shown when Gmail connector exists.
                Handles the common case where the user connected Gmail before
                we added the Calendar scope: prompts them to reconnect so the
                Morning Briefing can fetch their agenda. */}
            {gmailConnectors.length > 0 && calendarStatus && (
              <div
                className="px-4 py-3 border-t flex items-center gap-2.5 text-xs"
                style={{ borderColor: 'var(--border)' }}
              >
                {calendarStatus.state === 'connected' ? (
                  <>
                    <Calendar size={12} style={{ color: 'var(--accent)' }} />
                    <span style={{ color: 'var(--ink-muted)' }}>
                      Calendar activo — o briefing matinal inclui a tua agenda
                    </span>
                  </>
                ) : calendarStatus.state === 'missing_scope' ? (
                  <>
                    <AlertCircle size={12} style={{ color: 'var(--accent-warm)' }} />
                    <span style={{ color: 'var(--ink-light)' }}>
                      Calendar ainda não activo — reconecta para dares permissão
                    </span>
                    <button
                      onClick={() => connect('gmail')}
                      className="ml-auto text-xs rounded-md px-2.5 py-1 transition-colors"
                      style={{ background: 'var(--ink)', color: 'var(--cream)' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--accent)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'var(--ink)')}
                    >
                      Reconectar
                    </button>
                  </>
                ) : calendarStatus.state === 'error' ? (
                  <>
                    <AlertCircle size={12} style={{ color: '#dc2626' }} />
                    <span style={{ color: '#dc2626' }}>
                      Erro ao verificar Calendar
                      {calendarStatus.message ? ` — ${calendarStatus.message}` : ''}
                    </span>
                  </>
                ) : null}
              </div>
            )}

            {/* Histórico do calendário — bootstrap passivo.
                Só aparece quando o Calendar está realmente activo. Mostra
                "processado desde X" + CTA para estender a janela. Durante
                um run pending/running, a UI mostra progresso e faz poll. */}
            {gmailConnectors.length > 0 && calendarStatus?.state === 'connected' && calendarBackfill && (
              <div
                className="px-4 py-3 border-t flex items-center gap-2.5 text-xs flex-wrap"
                style={{ borderColor: 'var(--border)' }}
              >
                <Clock size={12} style={{ color: 'var(--ink-muted)' }} />
                {calendarBackfill.latest_status === 'running' || calendarBackfill.latest_status === 'pending' ? (
                  <span style={{ color: 'var(--ink-light)' }}>
                    A processar histórico do calendário…
                  </span>
                ) : calendarBackfill.has_any && calendarBackfill.processed_since ? (
                  <>
                    <span style={{ color: 'var(--ink-light)' }}>
                      Histórico processado desde {formatDate(calendarBackfill.processed_since)} · {calendarBackfill.events_ingested_total} reuniões
                    </span>
                    <div className="ml-auto relative">
                      <button
                        onClick={() => setExtendOpen(v => !v)}
                        disabled={extendBusy}
                        className="text-xs border rounded-md px-2.5 py-1 transition-colors"
                        style={{ borderColor: 'var(--border)', color: 'var(--ink-muted)' }}
                        onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink-light)')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-muted)')}
                      >
                        Estender
                      </button>
                      {extendOpen && (
                        <div
                          className="absolute right-0 mt-1 bg-white border rounded-md shadow-sm z-10 py-1 min-w-[140px]"
                          style={{ borderColor: 'var(--border)' }}
                        >
                          {[6, 12, 24].map(m => (
                            <button
                              key={m}
                              onClick={() => triggerBackfill(m)}
                              disabled={extendBusy}
                              className="w-full text-left text-xs px-3 py-1.5 transition-colors disabled:opacity-50"
                              style={{ color: 'var(--ink-light)' }}
                              onMouseEnter={e => (e.currentTarget.style.background = 'var(--cream)')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                              +{m} meses
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : calendarBackfill.latest_status === 'failed' ? (
                  <>
                    <span style={{ color: '#dc2626' }}>
                      Falha a processar histórico
                    </span>
                    <button
                      onClick={() => triggerBackfill(3)}
                      disabled={extendBusy}
                      className="ml-auto text-xs border rounded-md px-2.5 py-1 transition-colors"
                      style={{ borderColor: 'var(--border)', color: 'var(--ink-muted)' }}
                    >
                      Tentar de novo
                    </button>
                  </>
                ) : (
                  <>
                    <span style={{ color: 'var(--ink-muted)' }}>
                      Ainda sem histórico processado
                    </span>
                    <button
                      onClick={() => triggerBackfill(3)}
                      disabled={extendBusy}
                      className="ml-auto text-xs border rounded-md px-2.5 py-1 transition-colors"
                      style={{ borderColor: 'var(--border)', color: 'var(--ink-muted)' }}
                    >
                      Processar 3 meses
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Email (Postman) — ongoing ingestion status.
                Email has no backfill shape: the Postman agent runs on a
                30-min schedule between 9h and 20h. We consolidate
                last_run_at, recent run counts, and email-sourced memory
                counts into a single row so the user can see Postman is
                actually firing. Without this row users reasonably asked
                "o email já não está a alimentar o brain?" because calendar
                and contacts showed progress and email showed nothing. */}
            {gmailConnectors.length > 0 && emailStatus && emailStatus.state !== 'gmail_not_connected' && (
              <div
                className="px-4 py-3 border-t flex items-center gap-2.5 text-xs"
                style={{ borderColor: 'var(--border)' }}
              >
                <Mail size={12} style={{ color: emailStatus.state === 'active' ? 'var(--accent)' : 'var(--accent-warm)' }} />
                {emailStatus.state === 'active' ? (
                  <>
                    <span style={{ color: 'var(--ink-muted)' }}>
                      Triagem activa
                      {emailStatus.last_run_at
                        ? ` — última corrida ${formatRelative(emailStatus.last_run_at)}`
                        : ' — ainda não correu (primeira corrida a seguir)'}
                      {typeof emailStatus.emails_last_24h === 'number' && emailStatus.emails_last_24h > 0
                        ? ` · ${emailStatus.emails_last_24h} memórias nas últimas 24h`
                        : ''}
                    </span>
                    <button
                      onClick={triggerEmailRun}
                      disabled={emailBusy}
                      className="ml-auto text-xs border rounded-md px-2.5 py-1 transition-colors disabled:opacity-50"
                      style={{ borderColor: 'var(--border)', color: 'var(--ink-muted)' }}
                      onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink-light)')}
                      onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-muted)')}
                    >
                      {emailBusy ? 'A correr…' : 'Correr agora'}
                    </button>
                  </>
                ) : emailStatus.state === 'inactive' ? (
                  <span style={{ color: 'var(--ink-light)' }}>
                    Triagem desactivada — actualiza o agente Postman para retomar
                  </span>
                ) : emailStatus.state === 'no_postman' ? (
                  <span style={{ color: 'var(--ink-light)' }}>
                    Agente Postman não configurado — recarrega a página
                  </span>
                ) : null}
              </div>
            )}

            {/* Contactos (People API) — status + sync state.
                Pattern mirrors the calendar strips: first row = scope state
                (connected / missing_scope / error), second row = backfill
                state when the scope is granted. */}
            {gmailConnectors.length > 0 && contactsStatus && (
              <div
                className="px-4 py-3 border-t flex items-center gap-2.5 text-xs"
                style={{ borderColor: 'var(--border)' }}
              >
                {contactsStatus.state === 'connected' ? (
                  <>
                    <Users size={12} style={{ color: 'var(--accent)' }} />
                    <span style={{ color: 'var(--ink-muted)' }}>
                      Contactos activos
                      {contactsStatus.explicit && contactsStatus.other
                        ? ' — Google Contacts + Other Contacts'
                        : contactsStatus.explicit
                        ? ' — só Google Contacts'
                        : contactsStatus.other
                        ? ' — só Other Contacts'
                        : ''}
                    </span>
                  </>
                ) : contactsStatus.state === 'missing_scope' ? (
                  <>
                    <AlertCircle size={12} style={{ color: 'var(--accent-warm)' }} />
                    <span style={{ color: 'var(--ink-light)' }}>
                      Contactos ainda não activos — reconecta para dares permissão
                    </span>
                    <button
                      onClick={() => connect('gmail')}
                      className="ml-auto text-xs rounded-md px-2.5 py-1 transition-colors"
                      style={{ background: 'var(--ink)', color: 'var(--cream)' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--accent)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'var(--ink)')}
                    >
                      Reconectar
                    </button>
                  </>
                ) : contactsStatus.state === 'error' ? (
                  <>
                    <AlertCircle size={12} style={{ color: '#dc2626' }} />
                    <span style={{ color: '#dc2626' }}>
                      Erro ao verificar Contactos
                      {contactsStatus.message ? ` — ${contactsStatus.message}` : ''}
                    </span>
                  </>
                ) : null}
              </div>
            )}

            {/* Sincronização de contactos — só quando o scope está activo.
                Contactos não têm "meses" de histórico (é um snapshot); opção
                é "Re-sincronizar" para apanhar novos/actualizados. */}
            {gmailConnectors.length > 0 && contactsStatus?.state === 'connected' && contactsBackfill && (
              <div
                className="px-4 py-3 border-t flex items-center gap-2.5 text-xs flex-wrap"
                style={{ borderColor: 'var(--border)' }}
              >
                <Clock size={12} style={{ color: 'var(--ink-muted)' }} />
                {contactsBackfill.latest_status === 'running' || contactsBackfill.latest_status === 'pending' ? (
                  <span style={{ color: 'var(--ink-light)' }}>
                    A sincronizar contactos…
                  </span>
                ) : contactsBackfill.latest_status === 'failed' ? (
                  <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                    <div className="flex items-center gap-2.5">
                      <span style={{ color: '#dc2626' }}>
                        Falha a sincronizar contactos
                      </span>
                      <button
                        onClick={triggerContactsBackfill}
                        disabled={contactsBusy}
                        className="ml-auto text-xs border rounded-md px-2.5 py-1 transition-colors shrink-0"
                        style={{ borderColor: 'var(--border)', color: 'var(--ink-muted)' }}
                      >
                        Tentar de novo
                      </button>
                    </div>
                    {contactsBackfill.latest_error_message && (
                      <span className="text-xs break-words" style={{ color: 'var(--ink-muted)' }}>
                        {contactsBackfill.latest_error_message}
                      </span>
                    )}
                  </div>
                ) : contactsBackfill.has_any ? (
                  <>
                    <span style={{ color: 'var(--ink-light)' }}>
                      {contactsBackfill.events_ingested_total === 0 && (contactsBackfill.events_seen_total ?? 0) > 0 ? (
                        <>
                          {contactsBackfill.events_seen_total} vistos, 0 guardados — tudo filtrado como ruído
                        </>
                      ) : (
                        <>
                          {contactsBackfill.events_ingested_total} pessoas sincronizadas
                          {contactsBackfill.latest_run_at ? ` · última: ${formatDate(contactsBackfill.latest_run_at)}` : ''}
                        </>
                      )}
                    </span>
                    <button
                      onClick={triggerContactsBackfill}
                      disabled={contactsBusy}
                      className="ml-auto text-xs border rounded-md px-2.5 py-1 transition-colors"
                      style={{ borderColor: 'var(--border)', color: 'var(--ink-muted)' }}
                      onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink-light)')}
                      onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-muted)')}
                    >
                      Re-sincronizar
                    </button>
                  </>
                ) : (
                  <>
                    <span style={{ color: 'var(--ink-muted)' }}>
                      Contactos ainda não sincronizados
                    </span>
                    <button
                      onClick={triggerContactsBackfill}
                      disabled={contactsBusy}
                      className="ml-auto text-xs border rounded-md px-2.5 py-1 transition-colors"
                      style={{ borderColor: 'var(--border)', color: 'var(--ink-muted)' }}
                    >
                      Sincronizar agora
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
